import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import AuthForm from './AuthForm';
import {
  guestAccessSummary,
  guestMergeOnSignInNote,
} from './guestAccessCopy';
import { formatEliteGameCount, formatLessonCount } from './marketingStats';
import { LOGIN_LANDING_FEATURES } from './loginLandingFeatures';
import { usePublicStats } from './usePublicGameCount';
import { hasLoggedInBefore } from './priorLoginCookie';

type LoginLandingPageProps = {
  redirectedFrom?: string | null;
  onAuthenticated: () => void;
};

const pageLayoutSx = {
  display: 'grid',
  gap: { xs: 3, md: 5 },
  alignItems: 'start',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(320px, 440px)' },
} as const;

const heroGradientSx = {
  position: 'relative',
  overflow: 'hidden',
  borderRadius: { xs: 0, md: 4 },
  px: { xs: 2, sm: 4, md: 5 },
  py: { xs: 4, md: 6 },
  background:
    'radial-gradient(ellipse 80% 60% at 20% 0%, rgba(25, 118, 210, 0.22), transparent 60%), radial-gradient(ellipse 70% 50% at 100% 100%, rgba(156, 39, 176, 0.14), transparent 55%), linear-gradient(160deg, rgba(18, 18, 18, 0.2) 0%, rgba(18, 18, 18, 0.85) 100%)',
  border: { xs: 'none', md: '1px solid' },
  borderColor: { md: 'divider' },
} as const;

const statCardSx = {
  display: 'inline-flex',
  alignSelf: 'flex-start',
  width: 'fit-content',
  maxWidth: '100%',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 1.25,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'primary.dark',
  bgcolor: 'rgba(25, 118, 210, 0.08)',
} as const;

const featureGridSx = {
  display: 'grid',
  gap: 2,
  mt: { xs: 3, md: 4 },
  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
} as const;

const LoginLandingPage = ({
  redirectedFrom,
  onAuthenticated,
}: LoginLandingPageProps) => {
  const {
    gameCount: liveGameCount,
    lessonCount: liveLessonCount,
    loading: liveStatsLoading,
  } = usePublicStats();
  const showGuestMarketing = !hasLoggedInBefore();

  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 2, md: 4 },
        px: { xs: 0, md: 2 },
      }}
    >
      <Container maxWidth="lg">
        <Box sx={pageLayoutSx}>
          <Box>
            <Box sx={heroGradientSx}>
              <Stack spacing={3}>
                <Box>
                  <Chip
                    icon={<AutoGraphIcon />}
                    label="Master-level reference database"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Typography
                    component="h1"
                    variant="h3"
                    sx={{
                      fontWeight: 800,
                      letterSpacing: '-0.03em',
                      lineHeight: 1.1,
                      fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    }}
                  >
                    Built on real elite games
                  </Typography>
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{
                      mt: 2,
                      fontWeight: 400,
                      lineHeight: 1.5,
                      maxWidth: 560,
                    }}
                  >
                    Endchess is a chess training platform built on real elite games —
                    import and analyze your own from Chess.com and Lichess, not random
                    puzzles in a vacuum.
                  </Typography>
                </Box>

                <Stack spacing={1.5}>
                  <Box sx={statCardSx}>
                    {liveStatsLoading ? (
                      <Skeleton
                        variant="text"
                        width={72}
                        sx={{ fontSize: '2.125rem', transform: 'none' }}
                      />
                    ) : liveGameCount != null ? (
                      <Typography
                        component="span"
                        variant="h4"
                        color="primary.light"
                        sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}
                      >
                        {formatEliteGameCount(liveGameCount)}
                      </Typography>
                    ) : null}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4 }}
                    >
                      elite rapid and classical games
                    </Typography>
                  </Box>
                  {(liveStatsLoading || liveLessonCount != null) && (
                    <Box sx={statCardSx}>
                      {liveStatsLoading ? (
                        <Skeleton
                          variant="text"
                          width={72}
                          sx={{ fontSize: '2.125rem', transform: 'none' }}
                        />
                      ) : liveLessonCount != null ? (
                        <Typography
                          component="span"
                          variant="h4"
                          color="primary.light"
                          sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}
                        >
                          {formatLessonCount(liveLessonCount)}
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4 }}
                      >
                        published course lessons
                      </Typography>
                    </Box>
                  )}
                </Stack>

                <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
                  Explore what masters actually play. Import your games from Chess.com
                  and Lichess, analyze them move by move, and replay full games.
                  See engine refutations when you miss, drill with spaced repetition,
                  and study structured courses — all grounded in high-level practical chess.
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {showGuestMarketing && (
                    <>
                      {guestAccessSummary} {guestMergeOnSignInNote}{' '}
                    </>
                  )}
                  Free trial includes full explorer access.{' '}
                  <Box
                    component={RouterLink}
                    to="/subscribe"
                    sx={{ color: 'primary.light', textDecoration: 'none', fontWeight: 600 }}
                  >
                    See plans
                  </Box>
                </Typography>
              </Stack>
            </Box>

            <Box sx={featureGridSx}>
              {LOGIN_LANDING_FEATURES.map((feature) => {
                const Icon = feature.icon;
                return (
                  <Card
                    key={feature.title}
                    variant="outlined"
                    sx={{
                      height: '100%',
                      bgcolor: 'rgba(255, 255, 255, 0.02)',
                      transition: 'border-color 0.2s ease',
                      '&:hover': {
                        borderColor: 'primary.dark',
                      },
                    }}
                  >
                    <CardContent>
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Box
                          sx={{
                            mt: 0.25,
                            color: 'primary.light',
                            display: 'flex',
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {feature.description}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              position: { md: 'sticky' },
              top: { md: 24 },
            }}
          >
            <AuthForm
              redirectedFrom={redirectedFrom}
              onAuthenticated={onAuthenticated}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginLandingPage;
